import {
  Box,
  LinearProgress,
  linearProgressClasses,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { UnreadCount } from "../../../models/interfaces";
import {
  selectChatLoading,
  selectChatUsers,
  selectSelectedUser,
  selectUnreadCounts,
  setSelectedUserAction,
  setUnreadCountsAction,
} from "../../../redux/authSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { LightTooltip } from "../../../utils/styles";
import appDrawerStyles from "./AppDrawer.styles";

const BorderLinearProgress = styled(LinearProgress)(({ theme, value = 0 }) => {
  let barColor;
  if (value > 70) {
    barColor = "#2cc32c";
  } else if (value >= 50) {
    barColor = "#ffeb3b";
  } else {
    barColor = "#ff0000";
  }

  return {
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[200],
      ...theme.applyStyles("dark", {
        backgroundColor: theme.palette.grey[800],
      }),
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: barColor,
    },
  };
});

export interface Users {
  username: string;
  firstName: string;
  lastName: string;
  percentage: string;
  nickname: string;
  gender: number;
  name?: string;
}

const UsersDrawerItem = () => {
  const classes = appDrawerStyles;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selectedUser = useAppSelector(selectSelectedUser);
  const chatUsers = useAppSelector(selectChatUsers);
  const chatLoading = useAppSelector(selectChatLoading);
  const unreadCounts = useAppSelector(selectUnreadCounts);

  const handleSelectedUserChange = (user: Users) => {
    const updatedCounts = unreadCounts?.map((unread) =>
      unread.target === user.username ? { ...unread, count: 0 } : unread
    );
    dispatch(setSelectedUserAction({ selectedUser: user }));
    dispatch(setUnreadCountsAction({ unreadCounts: updatedCounts }));
  };

  const getUserBox = (sortedUserList: Users[], unreadCounts: UnreadCount[]) => {
    return (
      <Box sx={classes.userListSection}>
        {sortedUserList?.map((user: Users) => {
          const unreadCount =
            unreadCounts?.find((unread) => unread.target === user.username)
              ?.count || 0;
          {
            return (
              <LightTooltip
                title={user.nickname}
                placement="top"
                sx={{
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "#ffffff",
                    fontSize: "20px",
                    color: "#000000",
                  },
                }}
              >
                <Stack
                  key={user.username}
                  direction="column"
                  spacing={2}
                  sx={
                    user.username === selectedUser.username
                      ? classes.selectedUserStack
                      : classes.userStack
                  }
                  onClick={() => handleSelectedUserChange(user)}
                >
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Box sx={classes.nicknameContainer}>
                      <Typography sx={classes.nickname}>
                        {user.nickname}
                      </Typography>
                      <Box
                        sx={
                          user.gender === 1
                            ? { ...classes.male, ...classes.genderBox }
                            : { ...classes.female, ...classes.genderBox }
                        }
                      >
                        {user.gender === 1 ? "M" : "F"}
                      </Box>
                    </Box>
                    {unreadCount > 0 && (
                      <Box sx={classes.unreadCount}>{unreadCount}</Box>
                    )}
                  </Stack>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Box sx={{ flexGrow: 1, mr: 2 }}>
                      <BorderLinearProgress
                        variant="determinate"
                        value={+user.percentage}
                      />
                    </Box>
                    <Typography sx={{ ...classes.username, fontSize: "16px" }}>
                      {user.percentage + "%"}
                    </Typography>
                  </Stack>
                </Stack>
              </LightTooltip>
            );
          }
        })}
      </Box>
    );
  };

  const getUsersDrawerItem = () => {
    const sortedChatUsers = [...(chatUsers || [])].sort((a, b) => {
      const aUnread =
        unreadCounts?.find((unread) => unread.target === a.username)?.count ||
        0;
      const bUnread =
        unreadCounts?.find((unread) => unread.target === b.username)?.count ||
        0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      return +b.percentage - +a.percentage;
    });

    const hasChatUsers = chatUsers && chatUsers.length > 0;
    const chatUsersHeight = "100%";

    return (
      <Stack
        direction="column"
        spacing={1}
        py={2}
        px={1}
        height={"calc(100% - 32px)"}
      >
        {!chatLoading && (
          <>
            {hasChatUsers && (
              <Box sx={{ height: chatUsersHeight, ...classes.scrollBox }}>
                <Box sx={classes.sectionContainer}>
                  {getUserBox(sortedChatUsers, unreadCounts)}
                </Box>
              </Box>
            )}
          </>
        )}
        {chatLoading && (
          <Typography
            sx={{ ...classes.username, fontSize: "25px" }}
            mx={2.5}
            height={"100%"}
            alignContent={"center"}
          >
            {t("findingMatch")}
            <Typography sx={classes.typingIndicator}>...</Typography>
          </Typography>
        )}
      </Stack>
    );
  };

  return getUsersDrawerItem();
};

export default UsersDrawerItem;
